import React, { useEffect, useState } from 'react';
import { Calendar, CheckCircle, MapPin, Users, DollarSign, Gift, Camera, ArrowRight, Heart, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';
import { supabase } from '../lib/supabase';

const Dashboard = () => {
  const [currentPhase, setCurrentPhase] = useState(1);
  const [tasks, setTasks] = useState<Record<number, Array<{id: number, title: string, completed: boolean}>>>({});
  const [weddingDate, setWeddingDate] = useState<string | null>(null);
  const [countdown, setCountdown] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
  const [attendingGuestsCount, setAttendingGuestsCount] = useState(0);
  const [totalTasks] = useState(15);

  useEffect(() => {
    setTasks({
      1: [
        { id: 1, title: 'Set your wedding date', completed: false },
        { id: 2, title: 'Determine your budget', completed: false },
        { id: 3, title: 'Create your guest list', completed: false },
        { id: 4, title: 'Start venue research', completed: false }
      ],
      2: [
        { id: 5, title: 'Book your venue', completed: false },
        { id: 6, title: 'Hire key vendors', completed: false },
        { id: 7, title: 'Order your dress', completed: false },
        { id: 8, title: 'Plan engagement party', completed: false }
      ],
      3: [
        { id: 9, title: 'Send invitations', completed: false },
        { id: 10, title: 'Plan menu and tastings', completed: false },
        { id: 11, title: 'Choose flowers and decor', completed: false },
        { id: 12, title: 'Book honeymoon', completed: false }
      ],
      4: [
        { id: 13, title: 'Final headcount', completed: false },
        { id: 14, title: 'Rehearsal dinner', completed: false },
        { id: 15, title: 'Day-of timeline', completed: false }
      ]
    });

    loadWeddingDate();
    fetchAttendingGuestsCount();
  }, []);

  useEffect(() => {
    if (weddingDate) {
      const interval = setInterval(updateCountdown, 1000);
      return () => clearInterval(interval);
    }
  }, [weddingDate]);

  const loadWeddingDate = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('registries')
        .select('event_date')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      if (!error && data) {
        setWeddingDate(data.event_date);
      }
    } catch (error) {
      console.error('Error loading wedding date:', error);
    }
  };

  const fetchAttendingGuestsCount = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: registries, error: registriesError } = await supabase
        .from('registries')
        .select('id')
        .eq('user_id', user.id);

      if (registriesError) throw registriesError;
      if (!registries || registries.length === 0) return;

      const registryIds = registries.map(r => r.id);

      const { count, error: rsvpError } = await supabase
        .from('registry_rsvps')
        .select('*', { count: 'exact', head: true })
        .in('registry_id', registryIds)
        .eq('status', 'attending');

      if (rsvpError) throw rsvpError;
      setAttendingGuestsCount(count || 0);
    } catch (error) {
      console.error('Error fetching attending guests count:', error);
    }
  };

  const updateCountdown = () => {
    if (!weddingDate) return;

    const now = new Date().getTime();
    const weddingTime = new Date(weddingDate).getTime();
    const difference = weddingTime - now;

    if (difference > 0) {
      const days = Math.floor(difference / (1000 * 60 * 60 * 24));
      const hours = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((difference % (1000 * 60)) / 1000);

      setCountdown({ days, hours, minutes, seconds });
    } else {
      setCountdown({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    }
  };

  const toggleTask = (phaseId: number, taskId: number) => {
    setTasks(prev => ({
      ...prev,
      [phaseId]: prev[phaseId].map(task =>
        task.id === taskId ? { ...task, completed: !task.completed } : task
      )
    }));
  };

  const completedTasks = Object.values(tasks).flat().filter(task => task.completed).length;

  const phases = [
    {
      id: 1,
      title: '12+ Months',
      subtitle: 'Dream & Plan',
      description: 'Set your vision and start the journey',
    },
    {
      id: 2,
      title: '12-5 Months',
      subtitle: 'Book & Budget',
      description: 'Secure your venue and vendors',
    },
    {
      id: 3,
      title: '7-3 months',
      subtitle: 'Details & Design',
      description: 'Plan the details and send invitations',
    },
    {
      id: 4,
      title: '2-0 months',
      subtitle: 'Final Touches',
      description: 'Finalize everything for the big day',
    }
  ];

  const quickActions = [
    {
      title: 'Find Venues',
      description: 'Browse beautiful wedding venues',
      icon: MapPin,
      link: '/venues',
    },
    {
      title: 'Find Vendors',
      description: 'Discover trusted wedding professionals',
      icon: Camera,
      link: '/vendors',
    },
    {
      title: 'Manage Guests',
      description: 'Create your guest list and send invitations',
      icon: Users,
      link: '/create',
    },
    {
      title: 'Plan Budget',
      description: 'Track expenses and stay on budget',
      icon: DollarSign,
      link: '/budget',
    },
    {
      title: 'Gift Registry',
      description: 'Create and manage your wedding registry',
      icon: Gift,
      link: '/create',
    },
    {
      title: 'Day-of Planning',
      description: 'Organize seating and final details',
      icon: Calendar,
      link: '/day-of-planning',
    }
  ];

  const progressPercentage = (completedTasks / totalTasks) * 100;

  return (
    <div className="max-w-7xl mx-auto space-y-12">
      {/* Welcome Header */}
      <div className="text-center space-y-6 animate-fade-in">
        <h1 className="text-4xl md:text-5xl font-poppins font-bold text-gray-800">
          Welcome to Your
          <span className="block text-gradient">Wedding Journey</span>
        </h1>
        <p className="text-xl text-gray-600 max-w-3xl mx-auto font-poppins leading-relaxed">
          Let's transform your dream wedding into reality. Our comprehensive platform guides you through every elegant detail of your special day.
        </p>
      </div>

      {/* Wedding Countdown */}
      <div className="card-pastel p-12 text-center animate-fade-in relative overflow-hidden">
        <h2 className="text-3xl font-poppins font-bold text-gray-800 mb-2">Your Big Day Countdown</h2>
        <p className="text-gray-600 mb-8 font-poppins">The magic is almost here!</p>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 sm:gap-6 max-w-lg mx-auto">
          <div className="bg-pastel-purple-100 rounded-lg p-3">
            <p className="text-3xl font-bold text-pastel-purple-700">{countdown.days}</p>
            <p className="text-sm text-gray-600 font-poppins">Days</p>
          </div>
          <div className="bg-pastel-blue-100 rounded-lg p-3">
            <p className="text-3xl font-bold text-pastel-blue-700">{countdown.hours}</p>
            <p className="text-sm text-gray-600 font-poppins">Hours</p>
          </div>
          <div className="bg-pastel-green-100 rounded-lg p-3">
            <p className="text-3xl font-bold text-pastel-green-700">{countdown.minutes}</p>
            <p className="text-sm text-gray-600 font-poppins">Minutes</p>
          </div>
          <div className="bg-pastel-yellow-100 rounded-lg p-3">
            <p className="text-3xl font-bold text-pastel-yellow-700">{countdown.seconds}</p>
            <p className="text-sm text-gray-600 font-poppins">Seconds</p>
          </div>
        </div>
        <p className="mt-8 text-gray-600 font-poppins text-lg">
          {weddingDate ?
            `Your magical day is ${new Date(weddingDate).toLocaleDateString()}!` :
            'Create a registry to set your wedding date and start the countdown!'
          }
        </p>
      </div>

      {/* Progress Overview */}
      <div className="card-pastel p-8 animate-fade-in">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-poppins font-bold text-gray-800">Your Wedding Plan</h2>
          <div className="text-right">
            <p className="text-3xl font-bold text-pastel-purple-600">{completedTasks}/{totalTasks}</p>
            <p className="text-sm text-gray-500 font-poppins">tasks completed</p>
          </div>
        </div>

        <div className="w-full bg-gray-200 rounded-full h-4 mb-6 overflow-hidden">
          <div
            className="bg-gradient-to-r from-pastel-purple-400 to-pastel-blue-400 h-4 rounded-full transition-all duration-500"
            style={{ width: `${progressPercentage}%` }}
          />
        </div>

        <p className="text-gray-600 text-center font-poppins">
          {progressPercentage < 25 ? "Just getting started - you've got this!" :
           progressPercentage < 50 ? "Great progress! Keep up the momentum!" :
           progressPercentage < 75 ? "You're doing amazing! Almost there!" :
           "Wow! You're so close to your perfect day!"
          }
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 animate-fade-in">
        {/* Timeline Section */}
        <div className="lg:col-span-1">
          <div className="card-pastel p-8">
            <h3 className="text-xl font-poppins font-bold text-gray-800 mb-8">Planning Timeline</h3>

            <div className="space-y-4">
              {phases.map((phase) => (
                <button
                  key={phase.id}
                  onClick={() => setCurrentPhase(phase.id)}
                  className={`w-full text-left p-6 rounded-2xl border-2 transition-all duration-300 ${
                    currentPhase === phase.id
                      ? 'bg-pastel-purple-100 border-pastel-purple-300 text-gray-800 shadow-soft-lg transform scale-105'
                      : 'bg-white/50 border-white/40 text-gray-600 hover:bg-white/70 hover:border-pastel-purple-200'
                  }`}
                >
                  <div className="flex items-center space-x-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold transition-all duration-300 ${
                      currentPhase === phase.id ? 'bg-pastel-purple-500 text-white' : 'bg-gray-200 text-gray-600'
                    }`}>
                      {phase.id}
                    </div>
                    <div>
                      <p className="font-poppins font-semibold">{phase.title}</p>
                      <p className="text-sm text-gray-500 font-poppins">{phase.subtitle}</p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Current Phase Tasks */}
        <div className="lg:col-span-2">
          <div className="card-pastel p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-poppins font-bold text-gray-800">
                {phases.find(p => p.id === currentPhase)?.subtitle} Tasks
              </h3>
              <span className="px-4 py-2 bg-pastel-purple-100 border border-pastel-purple-200 text-pastel-purple-700 rounded-full text-sm font-medium font-poppins">
                Phase {currentPhase}
              </span>
            </div>

            <div className="space-y-3">
              {tasks[currentPhase]?.map((task) => (
                <div
                  key={task.id}
                  className="flex items-center space-x-4 p-4 rounded-xl hover:bg-white/50 transition-all duration-300"
                >
                  <button
                    onClick={() => {
                      toggleTask(currentPhase, task.id);
                    }}
                    className={`w-7 h-7 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${
                      task.completed
                        ? 'bg-pastel-green-500 border-pastel-green-500 text-white'
                        : 'border-gray-300 hover:border-pastel-purple-400 text-gray-400'
                    }`}
                  >
                    {task.completed && <CheckCircle className="w-5 h-5" />}
                  </button>
                  <span className={`flex-1 font-poppins ${task.completed ? 'line-through text-gray-400' : 'text-gray-700'}`}>
                    {task.title}
                  </span>
                </div>
              ))}
            </div>

            <div className="mt-8 p-6 bg-pastel-purple-50 border border-pastel-purple-100 rounded-xl">
              <p className="text-pastel-purple-700 text-sm text-center font-poppins">
                <Sparkles className="w-4 h-4 inline mr-2" />
                <strong>Tip:</strong> {phases.find(p => p.id === currentPhase)?.description}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Quick Actions Grid */}
      <div className="space-y-6">
        <h2 className="text-3xl font-poppins font-bold text-gray-800 text-center">Premium Planning Tools</h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {quickActions.map((action, index) => (
            <Link
              key={index}
              to={action.link}
              className="group card-pastel p-8 hover:scale-105 transition-all duration-500 animate-fade-in"
              style={{ animationDelay: `${index * 0.1}s` }}
            >
              <div className="flex items-start space-x-6">
                <div className="w-14 h-14 bg-gradient-to-br from-pastel-purple-300 to-pastel-blue-300 rounded-2xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                  <action.icon className="w-7 h-7 text-white" />
                </div>
                <div>
                  <h3 className="font-poppins font-semibold text-gray-800 text-lg mb-2">{action.title}</h3>
                  <p className="text-gray-600 font-poppins leading-relaxed">{action.description}</p>
                  <div className="flex items-center mt-4 text-pastel-purple-600 group-hover:translate-x-2 transition-transform duration-300">
                    <span className="text-sm font-medium font-poppins">Explore</span>
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Motivational Message */}
      <div className="card-pastel p-8 text-center animate-fade-in">
        <Heart className="w-16 h-16 mx-auto mb-6 text-pastel-pink-400" />
        <h3 className="text-2xl font-poppins font-semibold text-gray-800 mb-4">
          You're Creating Something Beautiful
        </h3>
        <p className="text-gray-600 font-poppins text-lg leading-relaxed max-w-2xl mx-auto">
          Take it one elegant step at a time. Every extraordinary wedding begins with thoughtful planning.
          You're crafting memories that will last a lifetime.
        </p>
      </div>
    </div>
  );
};

export default Dashboard;
